import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileDiff,
  FileWarning,
  Inbox,
  Loader2,
  RefreshCcw,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";

const deadlineLabel = {
  vencida: "Vencida",
  vence_7d: "≤ 7 dias",
  vence_30d: "≤ 30 dias",
  vence_60d: "≤ 60 dias",
  sem_prazo: "Sem prazo",
  futura: "Futura",
  ok: "Regular",
  aguardando_validacao: "Aguardando validação",
};

const typeLabel = {
  action: "Plano de ação",
  evidence: "Evidência",
  task: "Pendência interna",
  visit: "Visita",
  follow_up: "Follow-up",
  training: "Treinamento",
};

function SummaryCard({ label, value, icon: Icon, danger = false }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="label-tech">{label}</div>
            <div
              className={`mt-1 font-mono text-2xl font-semibold ${danger ? "text-destructive" : ""}`}
            >
              {value}
            </div>
          </div>
          <Icon className="size-5 text-primary" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function OperationsQueue() {
  const { isAdmin } = useAuth();
  const [proofPlan, setProofPlan] = useState("");
  const [proofBusy, setProofBusy] = useState(false);
  const [reminderHealth, setReminderHealth] = useState(null);
  useEffect(() => {
    if (isAdmin)
      api
        .get("/operations/action-reminders/health")
        .then((r) => setReminderHealth(r.data))
        .catch(() => setReminderHealth(null));
  }, [isAdmin]);
  const downloadProof = async () => {
    setProofBusy(true);
    try {
      const r = await api.get(
        `/operations/action-plans/${proofPlan}/proof-document`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comprovacao-${proofPlan}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(
        "Não foi possível gerar o documento. Confira os formatos aprovados e os limites de tamanho.",
      );
    } finally {
      setProofBusy(false);
    }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "fila";
  const [tab, setTab] = useState(initialTab);
  const [queue, setQueue] = useState({ items: [], counts: {}, total: 0 });
  const [evidence, setEvidence] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [reviews, setReviews] = useState({});
  const [reviewing, setReviewing] = useState("");
  const [task, setTask] = useState({
    client_id: "none",
    titulo: "",
    descricao: "",
    due_at: "",
    responsavel: "",
    prioridade: "media",
  });
  const [creatingTask, setCreatingTask] = useState(false);
  const [compareClient, setCompareClient] = useState("none");
  const [oldPlan, setOldPlan] = useState("");
  const [newPlan, setNewPlan] = useState("");
  const [diff, setDiff] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [q, e, t, p, c] = await Promise.all([
        api.get("/efficiency/work-queue?limit=500"),
        api.get("/efficiency/evidence/pending?limit=300"),
        api.get("/efficiency/tasks?status=pendente&limit=500"),
        api.get("/operations/action-plans"),
        api.get("/clients"),
      ]);
      setQueue(q.data);
      setEvidence(e.data);
      setTasks(t.data);
      setPlans(p.data);
      setClients(c.data);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Falha ao carregar a Central de Pendências",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const changeTab = (value) => {
    setTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === "fila") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  const visibleQueue = useMemo(
    () =>
      queue.items.filter((item) => {
        if (clientFilter !== "all" && item.client_id !== clientFilter)
          return false;
        if (typeFilter !== "all" && item.type !== typeFilter) return false;
        return true;
      }),
    [queue.items, clientFilter, typeFilter],
  );

  const plansForClient = useMemo(
    () => plans.filter((p) => p.client_id === compareClient),
    [plans, compareClient],
  );

  const reviewEvidence = async (item, approved) => {
    const state = reviews[item.id] || {};
    if (!approved && !(state.note || "").trim())
      return toast.error("Informe o motivo da recusa");
    setReviewing(item.id);
    try {
      await api.post(`/operations/evidence/${item.id}/review`, {
        aprovado: approved,
        observacao: state.note || "",
        validade_ate: approved ? state.validade_ate || null : null,
      });
      toast.success(
        approved
          ? "Evidência validada e ação atualizada"
          : "Evidência recusada; ação retornou para execução",
      );
      setReviews((prev) => ({ ...prev, [item.id]: {} }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao analisar evidência");
    } finally {
      setReviewing("");
    }
  };

  const downloadEvidence = async (item) => {
    try {
      const r = await api.get(`/operations/evidence/${item.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || "evidencia";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao baixar evidência");
    }
  };

  const createTask = async () => {
    if (!task.titulo.trim()) return toast.error("Informe a pendência");
    setCreatingTask(true);
    try {
      await api.post("/operations/tasks", {
        ...task,
        client_id: task.client_id === "none" ? null : task.client_id,
        due_at: task.due_at || null,
        source_type: "manual",
      });
      toast.success("Pendência adicionada à fila operacional");
      setTask({
        client_id: "none",
        titulo: "",
        descricao: "",
        due_at: "",
        responsavel: "",
        prioridade: "media",
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar pendência");
    } finally {
      setCreatingTask(false);
    }
  };

  const completeTask = async (id) => {
    try {
      await api.patch(`/operations/tasks/${id}/done`);
      toast.success("Pendência concluída");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao concluir pendência");
    }
  };

  const comparePlans = async () => {
    if (!oldPlan || !newPlan || oldPlan === newPlan)
      return toast.error("Selecione duas revisões diferentes");
    setComparing(true);
    setDiff(null);
    try {
      const r = await api.get(
        `/efficiency/action-plans/compare?old_plan_id=${encodeURIComponent(oldPlan)}&new_plan_id=${encodeURIComponent(newPlan)}`,
      );
      setDiff(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao comparar revisões");
    } finally {
      setComparing(false);
    }
  };

  return (
    <>
      <Header
        title="Conferências e pendências"
        subtitle="Uma única fila para saber o que precisa ser feito, para quem e até quando"
        actions={
          <Button variant="outline" onClick={load}>
            <RefreshCcw className="size-4" /> Atualizar
          </Button>
        }
      />
      <div
        className="page-container space-y-6"
        data-testid="operations-queue-page"
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            icon={Inbox}
            label="Itens na fila"
            value={queue.total || 0}
          />
          <SummaryCard
            icon={ShieldCheck}
            label="Evidências"
            value={queue.counts?.evidence || 0}
          />
          <SummaryCard
            icon={AlertTriangle}
            label="Ações prioritárias"
            value={queue.counts?.action || 0}
            danger={(queue.counts?.action || 0) > 0}
          />
          <SummaryCard
            icon={ClipboardCheck}
            label="Pendências internas"
            value={queue.counts?.task || 0}
          />
        </div>

        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="fila">
              <Inbox className="mr-2 size-4" />
              Fila única
            </TabsTrigger>
            <TabsTrigger value="evidencias">
              <UserRoundCheck className="mr-2 size-4" />
              Validar evidências
            </TabsTrigger>
            <TabsTrigger value="pendencias">
              <ClipboardCheck className="mr-2 size-4" />
              Pendências internas
            </TabsTrigger>
            <TabsTrigger value="revisoes">
              <FileDiff className="mr-2 size-4" />
              Revisões do PGR
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fila" className="mt-5 space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {Object.entries(typeLabel).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {loading && (
              <div className="h-32 animate-pulse rounded-2xl border bg-card/50" />
            )}
            {!loading &&
              visibleQueue.map((item) => (
                <Card
                  key={`${item.type}-${item.id}`}
                  className={
                    item.deadline_state === "vencida"
                      ? "border-destructive/35"
                      : ""
                  }
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant={
                              item.deadline_state === "vencida"
                                ? "destructive"
                                : "outline"
                            }
                          >
                            {deadlineLabel[item.deadline_state] ||
                              item.deadline_state}
                          </Badge>
                          <Badge variant="secondary">
                            {typeLabel[item.type] || item.type}
                          </Badge>
                          {item.client_name && (
                            <span className="text-xs text-muted-foreground">
                              {item.client_name}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 font-medium">{item.title}</div>
                        {item.subtitle && (
                          <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                            {item.subtitle}
                          </div>
                        )}
                        <div className="mt-2 text-xs text-muted-foreground">
                          Prazo/data:{" "}
                          {String(item.due_at || "—")
                            .slice(0, 16)
                            .replace("T", " ")}{" "}
                          · prioridade {item.priority || "—"}
                        </div>
                      </div>
                      <Link to={item.href || "/operacoes"}>
                        <Button size="sm" variant="outline">
                          Abrir <ArrowRight className="size-3.5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            {!loading && !visibleQueue.length && (
              <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                Nenhuma pendência para este filtro.
              </div>
            )}
          </TabsContent>

          <TabsContent value="evidencias" className="mt-5 space-y-4">
            {isAdmin && reminderHealth && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-semibold">Lembretes do plano de ação</h2>
                  <p className="mt-2 text-sm">
                    {reminderHealth.enabled && reminderHealth.days.length
                      ? `Ativados: ${reminderHealth.days.join(", ")} dias de antecedência.`
                      : "Desativados ou sem antecedências configuradas. Ajuste em Integrações → E-mails automáticos."}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Avisos reutilizam o link do cliente. Ações aguardando
                    análise não recebem cobrança automática.
                  </p>
                  {reminderHealth.needs_review.length > 0 && (
                    <p role="alert" className="mt-2 text-sm text-destructive">
                      {reminderHealth.needs_review.length} operações em envio ou
                      que exigem conferência. Consulte o histórico de e-mails
                      antes de reenviar.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            {isAdmin && (
              <Card>
                <CardContent className="p-5">
                  <h2 className="font-semibold">
                    Documento de comprovação do plano de ação
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Reúne o texto das ações, protocolos, pareceres e fotos/PDFs
                    aprovados. Arquivos pendentes ou recusados não são anexados.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <select
                      aria-label="Plano para documento de comprovação"
                      className="h-10 min-w-0 flex-1 rounded-md border bg-background px-3"
                      value={proofPlan}
                      onChange={(e) => setProofPlan(e.target.value)}
                    >
                      <option value="">Selecione o plano</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {
                            clients.find((c) => c.id === p.client_id)
                              ?.razao_social
                          }{" "}
                          · {p.nome}
                        </option>
                      ))}
                    </select>
                    <Button
                      disabled={!proofPlan || proofBusy}
                      onClick={downloadProof}
                    >
                      {proofBusy ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Download className="size-4" />
                      )}
                      Baixar comprovação em PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-4 text-sm text-muted-foreground">
              O cliente apenas envia a evidência.{" "}
              <strong className="text-foreground">
                A validação continua sendo técnica e interna.
              </strong>{" "}
              Aprovar pode definir uma validade/renovação; recusar exige
              justificativa.
            </div>
            {evidence.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-5">
                  <div className="flex flex-col gap-5 xl:flex-row xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{item.client_name}</Badge>
                        <Badge variant="secondary">{item.filename}</Badge>
                        <span className="font-mono text-[11px] text-muted-foreground">
                          SHA {String(item.sha256 || "").slice(0, 12)}…
                        </span>
                      </div>
                      <div className="mt-3 text-sm font-medium">
                        {item.action?.titulo_curto ||
                          item.action?.texto_original ||
                          "Ação"}
                      </div>
                      {item.note && (
                        <div className="mt-2 rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
                          Cliente: {item.note}
                        </div>
                      )}
                      <div className="mt-3 text-xs text-muted-foreground">
                        Enviada em{" "}
                        {String(item.submitted_at || "")
                          .slice(0, 16)
                          .replace("T", " ")}
                      </div>
                      <Button
                        className="mt-3"
                        size="sm"
                        variant="outline"
                        onClick={() => downloadEvidence(item)}
                      >
                        <Download className="size-3.5" /> Baixar arquivo
                      </Button>
                    </div>
                    <div className="w-full space-y-3 xl:w-[360px]">
                      <div>
                        <Label>Observação da análise</Label>
                        <Textarea
                          rows={3}
                          value={reviews[item.id]?.note || ""}
                          onChange={(e) =>
                            setReviews((p) => ({
                              ...p,
                              [item.id]: {
                                ...(p[item.id] || {}),
                                note: e.target.value,
                              },
                            }))
                          }
                          placeholder="Motivo da aprovação/recusa, ressalvas técnicas..."
                        />
                      </div>
                      <div>
                        <Label>Validade da evidência (opcional)</Label>
                        <Input
                          type="date"
                          value={reviews[item.id]?.validade_ate || ""}
                          onChange={(e) =>
                            setReviews((p) => ({
                              ...p,
                              [item.id]: {
                                ...(p[item.id] || {}),
                                validade_ate: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          variant="outline"
                          disabled={reviewing === item.id}
                          onClick={() => reviewEvidence(item, false)}
                        >
                          <FileWarning className="size-4" /> Recusar
                        </Button>
                        <Button
                          disabled={reviewing === item.id}
                          onClick={() => reviewEvidence(item, true)}
                        >
                          {reviewing === item.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="size-4" />
                          )}{" "}
                          Validar
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!evidence.length && (
              <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                Nenhuma evidência aguardando validação.
              </div>
            )}
          </TabsContent>

          <TabsContent value="pendencias" className="mt-5 space-y-5">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="label-tech">Nova pendência</div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Jogar para a fila, não para a memória
                  </h2>
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <Label>Título</Label>
                    <Input
                      value={task.titulo}
                      onChange={(e) =>
                        setTask((p) => ({ ...p, titulo: e.target.value }))
                      }
                      placeholder="Ex.: cobrar ASO pendente"
                    />
                  </div>
                  <div>
                    <Label>Cliente (opcional)</Label>
                    <Select
                      value={task.client_id}
                      onValueChange={(v) =>
                        setTask((p) => ({ ...p, client_id: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          Interno / sem cliente
                        </SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Responsável</Label>
                    <Input
                      value={task.responsavel}
                      onChange={(e) =>
                        setTask((p) => ({ ...p, responsavel: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Prazo</Label>
                    <Input
                      type="date"
                      value={task.due_at}
                      onChange={(e) =>
                        setTask((p) => ({ ...p, due_at: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Prioridade</Label>
                    <Select
                      value={task.prioridade}
                      onValueChange={(v) =>
                        setTask((p) => ({ ...p, prioridade: v }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["baixa", "media", "alta", "critica"].map((p) => (
                          <SelectItem key={p} value={p}>
                            {p}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 xl:col-span-1">
                    <Label>Descrição</Label>
                    <Textarea
                      rows={2}
                      value={task.descricao}
                      onChange={(e) =>
                        setTask((p) => ({ ...p, descricao: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={createTask} disabled={creatingTask}>
                    {creatingTask ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ClipboardCheck className="size-4" />
                    )}{" "}
                    Adicionar à fila
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-3">
              {tasks.map((item) => (
                <Card
                  key={item.id}
                  className={
                    item.deadline_state === "vencida"
                      ? "border-destructive/35"
                      : ""
                  }
                >
                  <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={
                            item.deadline_state === "vencida"
                              ? "destructive"
                              : "outline"
                          }
                        >
                          {deadlineLabel[item.deadline_state] ||
                            item.deadline_state}
                        </Badge>
                        {item.client_name && (
                          <Badge variant="secondary">{item.client_name}</Badge>
                        )}
                      </div>
                      <div className="mt-2 font-medium">{item.titulo}</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {item.descricao}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Prazo {item.due_at || "—"} ·{" "}
                        {item.responsavel || "sem responsável"} ·{" "}
                        {item.prioridade}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => completeTask(item.id)}>
                      <CheckCircle2 className="size-4" /> Concluir
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="revisoes" className="mt-5 space-y-5">
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4">
                  <div className="label-tech">PGR R01 × R02</div>
                  <h2 className="mt-1 text-lg font-semibold">
                    Comparar planos de ação sem apagar histórico
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    O CEO identifica ações mantidas, alteradas, novas e
                    removidas. O texto original de cada revisão é preservado.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label>Cliente</Label>
                    <Select
                      value={compareClient}
                      onValueChange={(v) => {
                        setCompareClient(v);
                        setOldPlan("");
                        setNewPlan("");
                        setDiff(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Revisão anterior</Label>
                    <Select
                      value={oldPlan}
                      onValueChange={setOldPlan}
                      disabled={compareClient === "none"}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {plansForClient.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.source_revision || p.nome} ·{" "}
                            {p.metrics?.total || 0} ações
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Revisão nova</Label>
                    <Select
                      value={newPlan}
                      onValueChange={setNewPlan}
                      disabled={compareClient === "none"}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {plansForClient.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.source_revision || p.nome} ·{" "}
                            {p.metrics?.total || 0} ações
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button onClick={comparePlans} disabled={comparing}>
                    {comparing ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <FileDiff className="size-4" />
                    )}{" "}
                    Comparar revisões
                  </Button>
                </div>
              </CardContent>
            </Card>
            {diff && (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <SummaryCard
                    label="Mantidas"
                    value={diff.summary.unchanged}
                    icon={CheckCircle2}
                  />
                  <SummaryCard
                    label="Alteradas"
                    value={diff.summary.changed}
                    icon={FileDiff}
                  />
                  <SummaryCard
                    label="Novas"
                    value={diff.summary.added}
                    icon={ClipboardCheck}
                  />
                  <SummaryCard
                    label="Removidas"
                    value={diff.summary.removed}
                    icon={FileWarning}
                  />
                </div>
                {diff.changed?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="font-semibold">Ações alteradas</h3>
                      <div className="mt-3 space-y-3">
                        {diff.changed.map((row, i) => (
                          <div key={i} className="rounded-xl border p-4">
                            <div className="mb-2 text-xs text-muted-foreground">
                              Similaridade{" "}
                              {Math.round((row.similarity || 0) * 100)}%
                            </div>
                            <div className="text-sm line-through opacity-65">
                              {row.old.texto_original}
                            </div>
                            <div className="mt-2 text-sm font-medium">
                              {row.new.texto_original}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {diff.added?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="font-semibold">Novas na revisão</h3>
                      <div className="mt-3 space-y-2">
                        {diff.added.map((row) => (
                          <div
                            key={row.id}
                            className="rounded-lg border border-primary/15 bg-primary/5 p-3 text-sm"
                          >
                            {row.texto_original}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {diff.removed?.length > 0 && (
                  <Card>
                    <CardContent className="p-5">
                      <h3 className="font-semibold">Removidas da revisão</h3>
                      <div className="mt-3 space-y-2">
                        {diff.removed.map((row) => (
                          <div
                            key={row.id}
                            className="rounded-lg border p-3 text-sm text-muted-foreground"
                          >
                            {row.texto_original}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
